import React, { useState, useEffect } from 'react';
import { useFieldArray, useFormContext, Controller } from 'react-hook-form';
import { X, Plus, User, Percent, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/Button';

interface BeneficiaryFormProps {
  name: string;
}

const BeneficiaryForm: React.FC<BeneficiaryFormProps> = ({ name }) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const { control, watch, setValue, formState: { errors }, trigger } = useFormContext();
  
  // Use field array to manage multiple beneficiaries
  const { fields, append, remove } = useFieldArray({
    control,
    name
  });

  // Watch for changes to beneficiary percentages
  const beneficiaries = watch(name) || [];
  const primaryBeneficiaries = beneficiaries.filter(b => b.type === 'Primary');
  const contingentBeneficiaries = beneficiaries.filter(b => b.type === 'Contingent');

  // Calculate total percentages
  const calculateTotal = (type: string) => {
    return beneficiaries
      .filter(b => b.type === type)
      .reduce((sum, b) => sum + (parseFloat(b.percentage) || 0), 0);
  };

  const primaryTotal = calculateTotal('Primary');
  const contingentTotal = calculateTotal('Contingent');

  // Validate percentages when they change
  useEffect(() => {
    if (beneficiaries.length > 0) {
      trigger(name);
    }
  }, [beneficiaries, trigger, name]);

  // Add a new beneficiary
  const handleAddBeneficiary = () => {
    if (!selectedType) return;
    
    append({
      type: selectedType,
      name: '',
      relationship: '',
      ssn: '',
      dateOfBirth: '',
      percentage: '',
      address: ''
    });
  };

  return (
    <div className="border rounded-lg p-4 bg-white">
      <div className="flex justify-between items-center cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <h3 className="text-md font-medium text-gray-800 flex items-center">
          Beneficiaries
          {(primaryTotal > 0 || contingentTotal > 0) && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({beneficiaries.length} added)
            </span>
          )}
        </h3>
        <button type="button" className="text-gray-400 hover:text-gray-600">
          {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </button>
      </div>

      {expanded && (
        <div className="mt-4 space-y-6">
          {/* Beneficiary Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Beneficiary Type
            </label>
            <div className="flex space-x-3">
              <select
                value={selectedType || ''}
                onChange={(e) => setSelectedType(e.target.value || null)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              >
                <option value="">Select Type</option>
                <option value="Primary">Primary Beneficiary</option>
                <option value="Contingent">Contingent Beneficiary</option>
              </select>
              <Button 
                type="button" 
                onClick={handleAddBeneficiary}
                disabled={!selectedType}
                variant="outline"
                className="flex-shrink-0"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </div>
            
            <div className="mt-2 flex items-start text-xs text-gray-500">
              <HelpCircle className="h-3.5 w-3.5 text-gray-400 mr-1 flex-shrink-0 mt-0.5" />
              <p>
                Primary beneficiaries receive assets first. Contingent beneficiaries receive assets only if no primary beneficiaries survive the account owner.
              </p>
            </div>
          </div>

          {/* Progress Indicator */}
          {beneficiaries.length > 0 && (
            <div className="space-y-3">
              {/* Primary Beneficiaries */}
              {primaryBeneficiaries.length > 0 && (
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-sm font-medium text-gray-700">Primary Beneficiaries</h4>
                    <div className="flex items-center">
                      <span className={`text-sm ${primaryTotal === 100 ? 'text-green-600' : 'text-red-600'}`}>
                        Total: {primaryTotal}%
                      </span>
                      {primaryTotal !== 100 && (
                        <span className="ml-2 text-xs text-red-600">
                          (Must equal 100%)
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full mb-4">
                    <div 
                      className={`h-2 rounded-full ${primaryTotal === 100 ? 'bg-green-500' : 'bg-red-500'}`} 
                      style={{ width: `${Math.min(primaryTotal, 100)}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Contingent Beneficiaries */}
              {contingentBeneficiaries.length > 0 && (
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-sm font-medium text-gray-700">Contingent Beneficiaries</h4>
                    <div className="flex items-center">
                      <span className={`text-sm ${contingentTotal === 100 ? 'text-green-600' : 'text-red-600'}`}>
                        Total: {contingentTotal}%
                      </span>
                      {contingentTotal !== 100 && contingentBeneficiaries.length > 0 && (
                        <span className="ml-2 text-xs text-red-600">
                          (Must equal 100%)
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full mb-4">
                    <div 
                      className={`h-2 rounded-full ${contingentTotal === 100 ? 'bg-green-500' : 'bg-red-500'}`} 
                      style={{ width: `${Math.min(contingentTotal, 100)}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Beneficiary List */}
          {fields.map((field, index) => {
            const fieldName = `${name}.${index}`;
            const fieldErrors = errors[name]?.[index];
            const beneficiaryType = watch(`${fieldName}.type`);
            
            return (
              <div key={field.id} className={`border rounded-lg p-4 ${beneficiaryType === 'Primary' ? 'border-blue-200 bg-blue-50' : 'border-purple-200 bg-purple-50'}`}>
                <div className="flex justify-between items-center mb-4">
                  <h4 className={`text-sm font-medium ${beneficiaryType === 'Primary' ? 'text-blue-800' : 'text-purple-800'}`}>
                    {beneficiaryType} Beneficiary
                  </h4>
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <Controller
                        control={control}
                        name={`${fieldName}.name`}
                        rules={{ required: 'Name is required' }}
                        render={({ field }) => (
                          <input
                            {...field}
                            type="text"
                            className={`pl-10 block w-full rounded-md shadow-sm sm:text-sm ${
                              fieldErrors?.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-primary focus:border-primary'
                            }`}
                            placeholder="John Smith"
                          />
                        )}
                      />
                      {fieldErrors?.name && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors.name.message}</p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Relationship <span className="text-red-500">*</span>
                    </label>
                    <Controller
                      control={control}
                      name={`${fieldName}.relationship`}
                      rules={{ required: 'Relationship is required' }}
                      render={({ field }) => (
                        <select
                          {...field}
                          className={`block w-full rounded-md shadow-sm sm:text-sm ${
                            fieldErrors?.relationship ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-primary focus:border-primary'
                          }`}
                        >
                          <option value="">Select Relationship</option>
                          <option value="Spouse">Spouse</option>
                          <option value="Child">Child</option>
                          <option value="Parent">Parent</option>
                          <option value="Sibling">Sibling</option>
                          <option value="Other Relative">Other Relative</option>
                          <option value="Trust">Trust</option>
                          <option value="Estate">Estate</option>
                          <option value="Charity">Charity</option>
                          <option value="Other">Other</option>
                        </select>
                      )}
                    />
                    {fieldErrors?.relationship && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.relationship.message}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      SSN (Last 4 digits)
                    </label>
                    <Controller
                      control={control}
                      name={`${fieldName}.ssn`}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="text"
                          maxLength={4}
                          placeholder="XXXX"
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                        />
                      )}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date of Birth
                    </label>
                    <Controller
                      control={control}
                      name={`${fieldName}.dateOfBirth`}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="date"
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                        />
                      )}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Percentage <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Controller
                        control={control}
                        name={`${fieldName}.percentage`}
                        rules={{ 
                          required: 'Percentage is required',
                          min: { value: 1, message: 'Minimum 1%' },
                          max: { value: 100, message: 'Maximum 100%' }
                        }}
                        render={({ field }) => (
                          <input
                            {...field}
                            type="number"
                            min="1"
                            max="100"
                            className={`block w-full rounded-md shadow-sm sm:text-sm ${
                              fieldErrors?.percentage ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-primary focus:border-primary'
                            }`}
                            placeholder="100"
                          />
                        )}
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <Percent className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                    {fieldErrors?.percentage && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.percentage.message}</p>
                    )}
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <Controller
                      control={control}
                      name={`${fieldName}.address`}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="text"
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                          placeholder="123 Main St, Anytown, ST 12345"
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            );
          })}

          {/* No beneficiaries message */}
          {fields.length === 0 && selectedType && (
            <div className="text-center py-6 border border-dashed border-gray-300 rounded-lg">
              <User className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No {selectedType.toLowerCase()} beneficiaries added yet</p>
              <Button 
                type="button" 
                onClick={handleAddBeneficiary} 
                variant="outline"
                className="mt-3"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add {selectedType} Beneficiary
              </Button>
            </div>
          )}

          {/* Validation messages */}
          {beneficiaries.length > 0 && (
            <div className="mt-4">
              {primaryBeneficiaries.length === 0 && (
                <p className="text-sm text-red-600 flex items-center">
                  <HelpCircle className="h-4 w-4 mr-1" />
                  At least one primary beneficiary is required
                </p>
              )}
              
              {primaryBeneficiaries.length > 0 && primaryTotal !== 100 && (
                <p className="text-sm text-red-600 flex items-center">
                  <HelpCircle className="h-4 w-4 mr-1" />
                  Primary beneficiary allocations must total exactly 100%
                </p>
              )}
              
              {contingentBeneficiaries.length > 0 && contingentTotal !== 100 && (
                <p className="text-sm text-red-600 flex items-center">
                  <HelpCircle className="h-4 w-4 mr-1" />
                  Contingent beneficiary allocations must total exactly 100%
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BeneficiaryForm;